import { useState, useEffect, useCallback } from 'react'
import cutoffsService from '@/services/cutoffsService'
import { useAuth } from './useAuth'
import type { CutoffsPayload } from '@/types'

interface UseCutoffsReturn {
  cutoffs: CutoffsPayload | null
  loading: boolean
  refresh: () => Promise<void>
}

/**
 * Fetches the per-scenario cutoff dates from the backend and caches them
 * in React state for the lifetime of the component. Backend is the source
 * of truth for the working-day rules; this hook is the only place the
 * frontend ever knows about them.
 *
 * No polling: cutoffs are stable across a session. Components can call
 * refresh() on demand (e.g. when a modal opens, to make sure the
 * minimum date reflects the current side of the 17:00 cutoff).
 */
export function useCutoffs(): UseCutoffsReturn {
  const { isAuthenticated } = useAuth()
  const [cutoffs, setCutoffs] = useState<CutoffsPayload | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchCutoffs = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const payload = await cutoffsService.getCutoffs()
      setCutoffs(payload)
    } catch (err) {
      console.error('Failed to fetch cutoffs:', err)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) {
      setCutoffs(null)
      setLoading(false)
      return
    }
    fetchCutoffs()
  }, [isAuthenticated, fetchCutoffs])

  return { cutoffs, loading, refresh: fetchCutoffs }
}

export default useCutoffs
